/*
 * @Author: Yumeng Xue
 * @Date: 2025-11-28
 * @Description: 
 * 轻量级分析引擎 (Analysis Engine)
 * 
 * 提供两个核心功能：
 * 1. UMAP 降维：将高维特征向量投影到 2D 平面，用于可视化
 * 2. MST 拓扑聚类：基于最小生成树的聚类分析，无需预设聚类数
 * 
 * 主要应用场景：
 * - 高维数据可视化
 * - 自动聚类发现
 * - 拓扑数据分析 (TDA)
 * - 异常检测
 * 
 * @FilePath: /topo-mess/src/core/analysis-engine.ts
 */

import { UMAP } from 'umap-js';

/**
 * MST 边的数据结构
 */
export interface MSTEdge {
    source: number;      // 源节点索引
    target: number;      // 目标节点索引
    weight: number;      // 边的权重（欧氏距离）
}

/**
 * MST 分析结果
 */
export interface MSTResult {
    edges: MSTEdge[];           // MST 的所有边
    persistence: number[];      // 边权重的排序数组（0-维持久性条形码）
    suggestedClusters: number;  // 基于 gap 启发式建议的聚类数
}

/**
 * UMAP 投影结果
 */
export interface ProjectionResult {
    coordinates: number[][];    // 2D 坐标 [[x1, y1], [x2, y2], ...]
    metadata: {
        nNeighbors: number;
        minDist: number;
        nEpochs: number;
    };
}

/**
 * 聚类结果
 */
export interface ClusteringResult {
    labels: number[];           // 每个点的聚类标签 [0, 1, 1, 0, 2, ...]
    numClusters: number;        // 聚类数量
    cutThreshold: number;       // 切断阈值（边权重）
}

/**
 * 轻量级分析引擎
 */
export class AnalysisEngine {

    // =========================================================================
    // 1. UMAP 降维
    // =========================================================================

    /**
     * 使用 UMAP 将高维向量降维到 2D
     * 
     * UMAP (Uniform Manifold Approximation and Projection) 优势：
     * - 比 t-SNE 更快，适合实时分析
     * - 保留全局结构和局部结构
     * - 参数调优简单
     * 
     * @param vectors - 高维特征向量矩阵 [N × D]
     * @param nNeighbors - 近邻数量（默认 15）
     *                     - 小值 (5-10): 保留局部结构
     *                     - 大值 (30-50): 保留全局结构
     * @param minDist - 最小距离（默认 0.1）
     *                  - 小值 (0.0-0.1): 紧密聚类
     *                  - 大值 (0.5-1.0): 松散分布
     * @param nEpochs - 迭代次数（默认 200）
     *                  - 少迭代: 快速但可能不收敛
     *                  - 多迭代: 慢但更准确
     * @returns ProjectionResult - 2D 坐标和元数据
     */
    public static runProjection(
        vectors: number[][],
        nNeighbors: number = 15,
        minDist: number = 0.1,
        nEpochs: number = 200
    ): ProjectionResult {

        if (vectors.length === 0) {
            return {
                coordinates: [],
                metadata: { nNeighbors, minDist, nEpochs }
            };
        }

        console.log(`Running UMAP projection on ${vectors.length} vectors...`);
        const startTime = performance.now();

        // ----------------------------------------------------------------------
        // 配置 UMAP（倾向于速度）
        // ----------------------------------------------------------------------
        const umap = new UMAP({
            nNeighbors: nNeighbors,      // 近邻数量
            minDist: minDist,            // 最小距离
            nEpochs: nEpochs,            // 迭代次数
            nComponents: 2,              // 降维到 2D
            random: Math.random,         // 随机种子函数
        });

        // ----------------------------------------------------------------------
        // 执行降维
        // ----------------------------------------------------------------------
        const coordinates = umap.fit(vectors);

        const elapsed = performance.now() - startTime;
        console.log(`UMAP projection completed in ${elapsed.toFixed(2)}ms`);

        return {
            coordinates,
            metadata: { nNeighbors, minDist, nEpochs }
        };
    }

    // =========================================================================
    // 2. MST 拓扑聚类
    // =========================================================================

    /**
     * 计算最小生成树 (Minimum Spanning Tree)
     * 
     * MST 在拓扑数据分析中的应用：
     * -----------------------------------------------------------------------
     * MST 是一种连接所有点的树，使得边的总权重最小。在聚类分析中：
     * 
     * 1. **0-维持久性**：
     *    - MST 的边权重对应于 0-维持久性条形码
     *    - 每条边的权重代表"合并两个连通分量需要的最小距离"
     * 
     * 2. **聚类检测**：
     *    - 初始状态：N 个点，N 个独立的集群
     *    - 按边权重从小到大连接：权重小的边先连接（近邻合并）
     *    - 权重大的边连接：远距离集群合并
     *    - **关键观察**：如果某条边的权重远大于前一条边，
     *      说明这是两个"明显分离"的集群的连接
     * 
     * 3. **自动确定聚类数 k**：
     *    - 切断 MST 中最长的 (k-1) 条边 → 得到 k 个集群
     *    - 例如：最长边 → 2 个集群，最长 2 条边 → 3 个集群
     * 
     * 4. **Gap 启发式**：
     *    - 计算相邻边权重的差值（gap）
     *    - 最大的 gap 对应于最佳的聚类分割点
     * 
     * 算法：Prim 算法
     * -----------------------------------------------------------------------
     * 时间复杂度：O(N²)（对于稠密图，适合小规模数据）
     * 空间复杂度：O(N²)（距离矩阵）
     * 
     * @param vectors - 高维特征向量矩阵 [N × D]
     * @returns MSTResult - MST 的边、持久性条形码、建议聚类数
     */
    public static computeMST(vectors: number[][]): MSTResult {

        const n = vectors.length;

        if (n === 0) {
            return { edges: [], persistence: [], suggestedClusters: 0 };
        }

        if (n === 1) {
            return { edges: [], persistence: [], suggestedClusters: 1 };
        }

        console.log(`Computing MST for ${n} points...`);
        const startTime = performance.now();

        // ----------------------------------------------------------------------
        // 步骤 1: 计算欧氏距离矩阵
        // ----------------------------------------------------------------------
        // dist[i][j] = 点 i 和点 j 之间的欧氏距离
        const distMatrix = this.computeDistanceMatrix(vectors);

        // ----------------------------------------------------------------------
        // 步骤 2: 使用 Prim 算法构建 MST
        // ----------------------------------------------------------------------
        // Prim 算法流程：
        // 1. 从任意节点开始（通常选择节点 0）
        // 2. 维护两个集合：
        //    - inMST: 已加入 MST 的节点
        //    - notInMST: 未加入 MST 的节点
        // 3. 重复 N-1 次：
        //    - 找到连接 inMST 和 notInMST 的最短边
        //    - 将该边加入 MST
        //    - 将目标节点加入 inMST

        const edges: MSTEdge[] = [];
        const inMST = new Set<number>();
        const minDist = new Array(n).fill(Infinity);  // 每个点到 MST 的最小距离
        const parent = new Array(n).fill(-1);         // 每个点的父节点

        // 从节点 0 开始
        minDist[0] = 0;

        for (let i = 0; i < n; i++) {
            // --------------------------------------------------------------
            // 2.1 找到 minDist 最小的未访问节点
            // --------------------------------------------------------------
            let u = -1;
            let minWeight = Infinity;

            for (let v = 0; v < n; v++) {
                if (!inMST.has(v) && minDist[v] < minWeight) {
                    u = v;
                    minWeight = minDist[v];
                }
            }

            if (u === -1) break; // 所有节点已访问

            // --------------------------------------------------------------
            // 2.2 将节点 u 加入 MST
            // --------------------------------------------------------------
            inMST.add(u);

            // 如果不是起始节点，添加边
            if (parent[u] !== -1) {
                edges.push({
                    source: parent[u],
                    target: u,
                    weight: minDist[u]
                });
            }

            // --------------------------------------------------------------
            // 2.3 更新相邻节点的 minDist
            // --------------------------------------------------------------
            for (let v = 0; v < n; v++) {
                if (!inMST.has(v) && distMatrix[u][v] < minDist[v]) {
                    minDist[v] = distMatrix[u][v];
                    parent[v] = u;
                }
            }
        }

        // ----------------------------------------------------------------------
        // 步骤 3: 计算持久性条形码（边权重排序）
        // ----------------------------------------------------------------------
        // 持久性条形码：按权重从小到大排序的边
        // 这对应于 0-维持久性图（persistence diagram）的 death 值
        const persistence = edges
            .map(e => e.weight)
            .sort((a, b) => a - b);

        // ----------------------------------------------------------------------
        // 步骤 4: 使用 Gap 启发式建议聚类数
        // ----------------------------------------------------------------------
        const suggestedClusters = this.suggestClusterCount(persistence);

        const elapsed = performance.now() - startTime;
        console.log(`MST computed in ${elapsed.toFixed(2)}ms`);
        console.log(`Suggested number of clusters: ${suggestedClusters}`);

        return { edges, persistence, suggestedClusters };
    }

    /**
     * 计算欧氏距离矩阵
     * 
     * @param vectors - 高维向量矩阵 [N × D]
     * @returns 距离矩阵 [N × N]
     */
    private static computeDistanceMatrix(vectors: number[][]): number[][] {
        const n = vectors.length;
        const distMatrix: number[][] = Array.from({ length: n }, () => new Array(n).fill(0));

        for (let i = 0; i < n; i++) {
            for (let j = i + 1; j < n; j++) {
                const dist = this.euclideanDistance(vectors[i], vectors[j]);
                distMatrix[i][j] = dist;
                distMatrix[j][i] = dist; // 对称矩阵
            }
        }

        return distMatrix;
    }

    /**
     * 计算欧氏距离
     * 
     * @param v1 - 向量 1
     * @param v2 - 向量 2
     * @returns 欧氏距离
     */
    private static euclideanDistance(v1: number[], v2: number[]): number {
        let sum = 0;
        for (let i = 0; i < v1.length; i++) {
            const diff = v1[i] - v2[i];
            sum += diff * diff;
        }
        return Math.sqrt(sum);
    }

    /**
     * 使用 Gap 启发式建议聚类数
     * 
     * 算法原理：
     * -----------------------------------------------------------------------
     * 1. 计算相邻边权重的差值（gap）：
     *    gap[i] = persistence[i+1] - persistence[i]
     * 
     * 2. 找到最大的 gap：
     *    - 最大 gap 对应于两个"明显分离"的集群
     *    - 在最大 gap 处切断，得到聚类数
     * 
     * 3. 聚类数 = gap 的位置 + 2
     *    - 例如：gap[5] 最大 → 切断第 6 条边 → 7 个集群
     * 
     * 特殊情况：
     * - 如果所有 gap 都很小 → 数据可能没有明显聚类 → 返回 1
     * - 如果点数太少 → 返回点数本身
     * 
     * @param persistence - 排序后的边权重数组
     * @returns 建议的聚类数
     */
    private static suggestClusterCount(persistence: number[]): number {
        const n = persistence.length + 1; // 点数 = 边数 + 1

        if (n <= 2) return n;

        // 计算相邻边的 gap
        const gaps: number[] = [];
        for (let i = 0; i < persistence.length - 1; i++) {
            gaps.push(persistence[i + 1] - persistence[i]);
        }

        // 找到最大的 gap
        let maxGap = 0;
        let maxGapIndex = 0;

        for (let i = 0; i < gaps.length; i++) {
            if (gaps[i] > maxGap) {
                maxGap = gaps[i];
                maxGapIndex = i;
            }
        }

        // 如果最大 gap 很小（相对于所有边权重），说明没有明显聚类
        const avgWeight = persistence.reduce((a, b) => a + b, 0) / persistence.length;
        if (maxGap < avgWeight * 0.1) {
            return 1; // 没有明显聚类
        }

        // 聚类数 = 在最大 gap 处切断后的分量数
        // maxGapIndex 对应的边后面切断，得到 maxGapIndex + 2 个集群
        const suggestedK = maxGapIndex + 2;

        // 限制最大聚类数（避免过度聚类）
        return Math.min(suggestedK, Math.floor(Math.sqrt(n)));
    }

    /**
     * 使用 K-Means 聚类
     * 
     * 原理：
     * -----------------------------------------------------------------------
     * 1. 随机初始化 k 个聚类中心
     * 2. 迭代优化：
     *    a. 分配：将每个点分配到最近的聚类中心
     *    b. 更新：重新计算每个聚类的中心点
     * 3. 重复直到收敛或达到最大迭代次数
     * 
     * @param vectors - 高维特征向量矩阵 [N × D]
     * @param k - 聚类数量
     * @param maxIterations - 最大迭代次数
     * @returns ClusteringResult - 聚类标签和元数据
     */
    public static clusterByKMeans(
        vectors: number[][],
        k: number,
        maxIterations: number = 100
    ): ClusteringResult {
        const n = vectors.length;

        if (n === 0 || k <= 0) {
            return { labels: [], numClusters: 0, cutThreshold: 0 };
        }

        if (k >= n) {
            return {
                labels: Array.from({ length: n }, (_, i) => i),
                numClusters: n,
                cutThreshold: 0
            };
        }

        const d = vectors[0].length;

        // 1. 初始化 k 个聚类中心（使用 k-means++）
        const centers = this.initKMeansPlusPlus(vectors, k);

        let labels = new Array(n).fill(0);
        let converged = false;

        // 2. 迭代优化
        for (let iter = 0; iter < maxIterations && !converged; iter++) {
            const newLabels = new Array(n);

            // 2a. 分配阶段：将每个点分配到最近的中心
            for (let i = 0; i < n; i++) {
                let minDist = Infinity;
                let bestCluster = 0;

                for (let j = 0; j < k; j++) {
                    const dist = this.euclideanDistance(vectors[i], centers[j]);
                    if (dist < minDist) {
                        minDist = dist;
                        bestCluster = j;
                    }
                }

                newLabels[i] = bestCluster;
            }

            // 检查收敛
            converged = newLabels.every((label, i) => label === labels[i]);
            labels = newLabels;

            if (converged) break;

            // 2b. 更新阶段：重新计算聚类中心
            const clusterSums = Array.from({ length: k }, () => new Array(d).fill(0));
            const clusterCounts = new Array(k).fill(0);

            for (let i = 0; i < n; i++) {
                const cluster = labels[i];
                clusterCounts[cluster]++;
                for (let j = 0; j < d; j++) {
                    clusterSums[cluster][j] += vectors[i][j];
                }
            }

            for (let i = 0; i < k; i++) {
                if (clusterCounts[i] > 0) {
                    for (let j = 0; j < d; j++) {
                        centers[i][j] = clusterSums[i][j] / clusterCounts[i];
                    }
                }
            }
        }

        // 计算最终的聚类质量指标（簇内平方和）
        let totalInertia = 0;
        for (let i = 0; i < n; i++) {
            const cluster = labels[i];
            const dist = this.euclideanDistance(vectors[i], centers[cluster]);
            totalInertia += dist * dist;
        }

        return {
            labels,
            numClusters: k,
            cutThreshold: totalInertia
        };
    }

    /**
     * K-Means++ 初始化
     * 
     * 选择距离已有中心较远的点作为新中心，避免局部最优
     */
    private static initKMeansPlusPlus(vectors: number[][], k: number): number[][] {
        const n = vectors.length;
        const centers: number[][] = [];

        // 随机选择第一个中心
        const firstIdx = Math.floor(Math.random() * n);
        centers.push([...vectors[firstIdx]]);

        // 选择剩余的 k-1 个中心
        for (let i = 1; i < k; i++) {
            const distances = new Array(n).fill(0);
            let totalDist = 0;

            // 计算每个点到最近中心的距离
            for (let j = 0; j < n; j++) {
                let minDist = Infinity;
                for (const center of centers) {
                    const dist = this.euclideanDistance(vectors[j], center);
                    minDist = Math.min(minDist, dist);
                }
                distances[j] = minDist * minDist;
                totalDist += distances[j];
            }

            // 按概率选择新中心（距离越远，概率越大）
            let rand = Math.random() * totalDist;
            let newIdx = 0;
            for (let j = 0; j < n; j++) {
                rand -= distances[j];
                if (rand <= 0) {
                    newIdx = j;
                    break;
                }
            }

            centers.push([...vectors[newIdx]]);
        }

        return centers;
    }

    /**
     * 自动确定最佳聚类数（使用肘部法则）
     * 
     * @param vectors - 高维特征向量矩阵
     * @param maxK - 最大聚类数（默认 8）
     * @returns 建议的聚类数
     */
    public static suggestKMeansK(vectors: number[][], maxK: number = 8): number {
        const n = vectors.length;

        if (n <= 2) return Math.min(n, maxK);

        // 限制 maxK 不超过点数
        const kMax = Math.min(maxK, Math.floor(n / 2));

        const inertias: number[] = [];

        // 计算不同 k 值的簇内平方和
        for (let k = 1; k <= kMax; k++) {
            const result = this.clusterByKMeans(vectors, k, 50);
            inertias.push(result.cutThreshold);
        }

        // 使用肘部法则：找到下降速率突变点
        if (inertias.length <= 2) return 2;

        const deltas: number[] = [];
        for (let i = 1; i < inertias.length; i++) {
            deltas.push(inertias[i - 1] - inertias[i]);
        }

        // 找到二阶导数最大的点
        let maxSecondDerivative = 0;
        let bestK = 2;

        for (let i = 1; i < deltas.length; i++) {
            const secondDerivative = Math.abs(deltas[i - 1] - deltas[i]);
            if (secondDerivative > maxSecondDerivative) {
                maxSecondDerivative = secondDerivative;
                bestK = i + 1;
            }
        }

        return bestK;
    }

    /**
     * 基于 MST 进行聚类
     * 
     * 原理：
     * -----------------------------------------------------------------------
     * 1. 对 MST 的边按权重从大到小排序
     * 2. 移除最长的 (k-1) 条边
     * 3. 剩余的边形成 k 个连通分量 → k 个集群
     * 4. 使用并查集（Union-Find）确定每个点的集群标签
     * 
     * @param mstResult - MST 计算结果
     * @param numClusters - 目标聚类数（如果为 0，使用建议值）
     * @returns ClusteringResult - 聚类标签和元数据
     */
    public static clusterByMST(
        mstResult: MSTResult,
        numClusters: number = 0
    ): ClusteringResult {

        // 使用建议的聚类数（如果未指定）
        const k = numClusters > 0 ? numClusters : mstResult.suggestedClusters;

        if (k <= 0) {
            return { labels: [], numClusters: 0, cutThreshold: 0 };
        }

        const n = mstResult.edges.length + 1; // 点数 = 边数 + 1

        // 特殊情况：只有一个点或聚类数 >= 点数
        if (n === 1 || k >= n) {
            return {
                labels: Array.from({ length: n }, (_, i) => i),
                numClusters: n,
                cutThreshold: 0
            };
        }

        // ----------------------------------------------------------------------
        // 步骤 1: 对边按权重从大到小排序
        // ----------------------------------------------------------------------
        const sortedEdges = [...mstResult.edges].sort((a, b) => b.weight - a.weight);

        // ----------------------------------------------------------------------
        // 步骤 2: 移除最长的 (k-1) 条边
        // ----------------------------------------------------------------------
        const edgesToKeep = sortedEdges.slice(k - 1);
        const cutThreshold = sortedEdges[k - 2]?.weight || 0;

        // ----------------------------------------------------------------------
        // 步骤 3: 使用并查集确定连通分量
        // ----------------------------------------------------------------------
        const uf = new UnionFind(n);

        for (const edge of edgesToKeep) {
            uf.union(edge.source, edge.target);
        }

        // ----------------------------------------------------------------------
        // 步骤 4: 分配聚类标签
        // ----------------------------------------------------------------------
        // 将根节点映射到聚类 ID
        const rootToCluster = new Map<number, number>();
        let clusterId = 0;

        const labels: number[] = [];
        for (let i = 0; i < n; i++) {
            const root = uf.find(i);
            if (!rootToCluster.has(root)) {
                rootToCluster.set(root, clusterId++);
            }
            labels.push(rootToCluster.get(root)!);
        }

        return {
            labels,
            numClusters: clusterId,
            cutThreshold
        };
    }

    /**
     * 可视化 MST 的持久性图
     * 
     * 生成用于绘制持久性条形码的数据
     * 
     * @param persistence - 边权重排序数组
     * @returns 条形码数据 [{birth, death, index}, ...]
     */
    public static generatePersistenceDiagram(persistence: number[]): {
        birth: number;
        death: number;
        index: number;
    }[] {
        return persistence.map((death, index) => ({
            birth: 0,           // 0-维特征在起始时刻诞生
            death: death,       // 合并时刻（边权重）
            index: index
        }));
    }
}

/**
 * 并查集 (Union-Find) 数据结构
 * 
 * 用于高效地维护连通分量
 * 
 * 操作：
 * - find(x): 查找 x 所在集合的根节点（O(α(n))，几乎常数时间）
 * - union(x, y): 合并 x 和 y 所在的集合（O(α(n))）
 * 
 * 优化：
 * - 路径压缩：在 find 操作时，将路径上的所有节点直接连接到根节点
 * - 按秩合并：将较小的树合并到较大的树上
 */
class UnionFind {
    private parent: number[];
    private rank: number[];

    constructor(size: number) {
        this.parent = Array.from({ length: size }, (_, i) => i);
        this.rank = new Array(size).fill(0);
    }

    /**
     * 查找节点 x 的根节点（带路径压缩）
     */
    find(x: number): number {
        if (this.parent[x] !== x) {
            this.parent[x] = this.find(this.parent[x]); // 路径压缩
        }
        return this.parent[x];
    }

    /**
     * 合并节点 x 和 y 所在的集合（按秩合并）
     */
    union(x: number, y: number): void {
        const rootX = this.find(x);
        const rootY = this.find(y);

        if (rootX === rootY) return; // 已经在同一集合

        // 按秩合并：将较小的树合并到较大的树
        if (this.rank[rootX] < this.rank[rootY]) {
            this.parent[rootX] = rootY;
        } else if (this.rank[rootX] > this.rank[rootY]) {
            this.parent[rootY] = rootX;
        } else {
            this.parent[rootY] = rootX;
            this.rank[rootX]++;
        }
    }
}
